import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, firstValueFrom, of } from 'rxjs';
import { TestSeriesService } from '../../features/test-series/services/test-series.service';
import { ExamEnvironmentCheckService } from './exam-environment-check.service';
import { ExamTabLockService } from './exam-tab-lock.service';
import { MockTestSecurityService } from './mock-test-security.service';
import { detectPrivateBrowsing } from './private-browsing.util';

export const mockTestSecurityGuard: CanActivateFn = () => {
  inject(MockTestSecurityService).activate();
  return true;
};

export const privateBrowsingGuard: CanActivateFn = async (_route, state) => {
  const router = inject(Router);
  const isPrivateBrowsing = await detectPrivateBrowsing();
  console.log('[PB-DEBUG] privateBrowsingGuard', state.url, '->', isPrivateBrowsing ? 'BLOCK' : 'ALLOW'); // TEMP-DEBUG

  if (isPrivateBrowsing) {
    // Keep the requested page so the blocked screen can hand the candidate a link to it.
    return router.createUrlTree(['/private-browsing-blocked'], { queryParams: { returnUrl: state.url } });
  }

  return true;
};

// Sends a normal window that lands on the blocked page (copied URL, reload) on to the page it was blocked from.
export const privateBrowsingOnlyGuard: CanActivateFn = async (route) => {
  const router = inject(Router);
  const isPrivateBrowsing = await detectPrivateBrowsing();

  return isPrivateBrowsing ? true : router.parseUrl(safeReturnUrl(route.queryParamMap.get('returnUrl')));
};

/** Only in-app paths are honoured; anything else falls back to the app root. */
export function safeReturnUrl(returnUrl: string | null): string {
  if (!returnUrl?.startsWith('/') || returnUrl.startsWith('//') || returnUrl.startsWith('/private-browsing-blocked')) {
    return '/';
  }

  return returnUrl;
}

/**
 * Keeps a second tab out of the exam flow while another tab is running the
 * exam. The tab is still sent somewhere harmless; the duplicate-tab overlay
 * (driven by the same service) then covers it until the exam tab closes.
 */
export const singleExamTabGuard: CanActivateFn = async () => {
  const router = inject(Router);
  const { examActiveElsewhere } = await inject(ExamTabLockService).probe();

  return examActiveElsewhere ? router.createUrlTree(['/test-series']) : true;
};

/**
 * Backstop for the start/resume popup checks: a bookmarked or typed exam URL
 * would otherwise skip them. Silently sends the user back to the test list;
 * the issues are only shown inside the start/resume popup.
 */
export const examEnvironmentGuard: CanActivateFn = async (route) => {
  const router = inject(Router);
  const testSeriesService = inject(TestSeriesService);
  const environmentCheck = inject(ExamEnvironmentCheckService);
  const examId = route.paramMap.get('id');
  // Proctoring-only checks are skipped if the exam details can't be loaded.
  const examDetails = examId
    ? await firstValueFrom(testSeriesService.getExamDetails(examId).pipe(catchError(() => of(null))))
    : null;
  const issues = await environmentCheck.findIssues(testSeriesService.extractExamSettings(examDetails).enableProctoring);

  return issues.length ? router.createUrlTree(['/test-series']) : true;
};
